/**
 * Word list editor — the parent-facing "set this week's words" surface.
 *
 * Per spec §3.3:
 *  - Single textarea, one word per line. Multi-word entries supported
 *    ("ice cream" on a line is one spelling). Blank lines dropped,
 *    leading/trailing whitespace trimmed.
 *  - Each parsed word gets an optional example-sentence field.
 *  - 1–12 words accepted; 10 typical.
 *  - Saving prompts to confirm overwrite if a current week exists (the
 *    previous current week is archived).
 *  - Gentle flag for sentences that contain the spelling word verbatim.
 *
 * Past-week editing (also in §3.3) is intentionally deferred — Phase 2's
 * roadmap line only commits to "save-as-current-week". The list of past
 * weeks shows up here as a read-only summary so the parent knows it's
 * there.
 */
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/Button";
import { api } from "@/lib/api";
import type { Week } from "@/types/data";
import "./WordEditor.css";

const WORD_COUNT_MIN = 1;
const WORD_COUNT_MAX = 12;
const TYPICAL_COUNT = 10;

interface WordRow {
  /** lower-cased + trimmed spelling, used as a stable identity key. */
  key: string;
  spelling: string;
  exampleSentence: string;
}

function parseSpellings(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Reconcile rows with the latest parsed spellings, preserving sentences. */
function reconcile(spellings: string[], existing: WordRow[]): WordRow[] {
  const byKey = new Map(existing.map((r) => [r.key, r]));
  return spellings.map((spelling) => {
    const key = spelling.toLowerCase();
    const prev = byKey.get(key);
    return {
      key,
      spelling,
      exampleSentence: prev?.exampleSentence ?? "",
    };
  });
}

export function WordEditor() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentWeek, setCurrentWeek] = useState<Week | null>(null);
  const [archivedCount, setArchivedCount] = useState(0);

  const [wordsText, setWordsText] = useState("");
  const [rows, setRows] = useState<WordRow[]>([]);

  const [newWeekMode, setNewWeekMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const refreshFromServer = useCallback(async () => {
    const { status, body } = await api.listWeeks();
    if (status !== 200 || !body) {
      setLoadError("Couldn't load the weeks list.");
      return;
    }
    setLoadError(null);
    setCurrentWeek(body.current);
    setArchivedCount(body.archived.length);
    if (body.current) {
      const text = body.current.words.map((w) => w.spelling).join("\n");
      setWordsText(text);
      setRows(
        body.current.words.map((w) => ({
          key: w.spelling.toLowerCase(),
          spelling: w.spelling,
          exampleSentence: w.exampleSentence ?? "",
        })),
      );
    } else {
      setWordsText("");
      setRows([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refreshFromServer();
      setLoading(false);
    })();
  }, [refreshFromServer]);

  const handleWordsChange = (value: string) => {
    setWordsText(value);
    setRows(reconcile(parseSpellings(value), rows));
    setSavedAt(null);
  };

  const handleSentenceChange = (key: string, value: string) => {
    setRows((current) =>
      current.map((r) => (r.key === key ? { ...r, exampleSentence: value } : r)),
    );
    setSavedAt(null);
  };

  const wordCount = rows.length;
  const countValid = wordCount >= WORD_COUNT_MIN && wordCount <= WORD_COUNT_MAX;

  function buildPayload() {
    return {
      words: rows.map((r) => ({
        spelling: r.spelling,
        exampleSentence: r.exampleSentence.trim().length > 0 ? r.exampleSentence.trim() : null,
      })),
    };
  }

  function describeError(body: unknown): string {
    if (
      body !== null &&
      typeof body === "object" &&
      "error" in body &&
      (body as { error?: unknown }).error === "invalid_body" &&
      "detail" in body
    ) {
      const detail = (body as { detail?: unknown }).detail;
      if (typeof detail === "string") return `Couldn't save: ${detail}.`;
    }
    return "Couldn't save. Try again.";
  }

  /** PUT — in-place edit of the existing current week. Preserves week.id
   *  and (where possible) word_entry.ids, so session attempt history
   *  survives label / sentence / typo fixes. */
  async function performInPlaceUpdate() {
    if (currentWeek === null) return;
    setSaving(true);
    setSaveError(null);
    const { status, body } = await api.updateWeek(currentWeek.id, buildPayload());
    setSaving(false);
    if (status === 200) {
      setSavedAt(Date.now());
      await refreshFromServer();
      return;
    }
    setSaveError(describeError(body));
  }

  /** POST — archive the existing current week and create a fresh one from
   *  the words now in the editor. Reached only via the "Save as this week"
   *  button, which is shown once the parent has started a new week (or when
   *  no current week exists yet). */
  async function performStartNewWeek() {
    setSaving(true);
    setSaveError(null);
    const { status, body } = await api.createWeek(buildPayload());
    setSaving(false);
    if (status === 201) {
      setSavedAt(Date.now());
      setNewWeekMode(false);
      await refreshFromServer();
      return;
    }
    setSaveError(describeError(body));
  }

  /** Clear the editor so the parent can type this week's new words. The
   *  current week is left untouched in the DB — it is only archived and
   *  replaced once they fill in the new words and hit "Save as this week"
   *  (performStartNewWeek). No confirm prompt: nothing is destroyed here. */
  function enterNewWeekMode() {
    if (saving) return;
    setNewWeekMode(true);
    setWordsText("");
    setRows([]);
    setSavedAt(null);
    setSaveError(null);
  }

  /** Back out of new-week entry and restore the current week's words. */
  async function cancelNewWeek() {
    if (saving) return;
    setNewWeekMode(false);
    setSaveError(null);
    setSavedAt(null);
    await refreshFromServer();
  }

  /** True when the save action should create a brand-new week (POST) rather
   *  than edit the current one in place (PUT): either there is no current
   *  week yet, or the parent has pressed "Start a new week". */
  const creatingNewWeek = currentWeek === null || newWeekMode;

  if (loading) {
    return <p className="word-editor__loading">Loading…</p>;
  }
  if (loadError !== null) {
    return (
      <div className="word-editor__error">
        <p>{loadError}</p>
        <Button
          variant="secondary"
          onClick={() => {
            void refreshFromServer();
          }}
        >
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="word-editor">
      {currentWeek !== null && !newWeekMode && (
        <div className="word-editor__top-actions">
          <Button variant="secondary" onClick={enterNewWeekMode} disabled={saving}>
            Start a new week
          </Button>
          <p className="word-editor__top-hint">
            New homework this week? The current words move to past weeks.
          </p>
        </div>
      )}

      {newWeekMode && (
        <div className="word-editor__new-week-banner">
          <p className="word-editor__new-week-text">
            Type this week&rsquo;s new words below, then <strong>Save as this week</strong>.
          </p>
          <Button
            variant="secondary"
            size="small"
            onClick={() => void cancelNewWeek()}
            disabled={saving}
          >
            Cancel
          </Button>
        </div>
      )}

      <section className="word-editor__section">
        <h2 className="word-editor__heading">This week&rsquo;s words</h2>
        <p className="word-editor__description">
          One spelling per line. Multi-word entries are fine — &ldquo;ice cream&rdquo; on a line
          is one spelling. {TYPICAL_COUNT} is typical, 1–{WORD_COUNT_MAX} accepted.
        </p>

        <label className="word-editor__label" htmlFor="words">
          Words ({wordCount}
          {countValid ? "" : ` — needs ${WORD_COUNT_MIN}–${WORD_COUNT_MAX}`})
        </label>
        <textarea
          id="words"
          className="word-editor__textarea"
          value={wordsText}
          onChange={(e) => handleWordsChange(e.target.value)}
          rows={12}
          spellCheck={false}
          autoCapitalize="none"
          autoCorrect="off"
        />
      </section>

      {rows.length > 0 && (
        <section className="word-editor__section">
          <h2 className="word-editor__heading">Example sentences (optional)</h2>
          <p className="word-editor__description">
            Audio-only memory hooks Eva can tap to hear in the flash card and spelling phases.
            Short and natural — under 7 words is ideal. Use the spelling word in the sentence
            if it sounds right (e.g. &ldquo;The cat sat on the mat&rdquo;); the word is always
            spoken aloud at the start of each spelling phase anyway.
          </p>
          <ul className="word-editor__rows">
            {rows.map((row) => (
              <li key={row.key} className="word-editor__row">
                <span className="word-editor__row-word">{row.spelling}</span>
                <input
                  type="text"
                  className="word-editor__row-sentence"
                  placeholder="Optional sentence"
                  value={row.exampleSentence}
                  onChange={(e) => handleSentenceChange(row.key, e.target.value)}
                  autoCapitalize="sentences"
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="word-editor__actions">
        {saveError !== null && <p className="word-editor__save-error">{saveError}</p>}
        {savedAt !== null && saveError === null && (
          <p className="word-editor__save-ok">Saved.</p>
        )}
        {creatingNewWeek ? (
          <div className="word-editor__action-row">
            <Button
              onClick={() => void performStartNewWeek()}
              disabled={!countValid || saving}
            >
              {saving ? "Saving…" : "Save as this week"}
            </Button>
          </div>
        ) : (
          <>
            <p className="word-editor__action-hint">
              Use <strong>Save changes</strong> to fix a typo or add a sentence to this
              week&rsquo;s words.
            </p>
            <div className="word-editor__action-row">
              <Button
                onClick={() => void performInPlaceUpdate()}
                disabled={!countValid || saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </>
        )}
      </section>

      <p className="word-editor__footnote">
        {archivedCount === 0
          ? "No archived weeks yet."
          : `${archivedCount} archived week${archivedCount === 1 ? "" : "s"} (review and editing land in a later phase).`}
      </p>
    </div>
  );
}
